import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * User-facing view for Form Input plugin - renders dynamic form fields and handles submission.
 */
import { useState } from "react";
/**
 * Renders a single form field based on its type.
 * @param root0 - The component props
 * @param root0.field - The field configuration
 * @param root0.value - Current field value
 * @param root0.onChange - Callback when value changes
 * @param root0.disabled - Whether the field is disabled
 * @returns The appropriate input component for the field type
 */
function FormFieldRenderer({ field, value, onChange, disabled }) {
    const baseInputClass = "w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";
    /**
     * Safely converts unknown value to string.
     * @param v - The value to convert
     * @returns String representation of the value
     */
    const toStringValue = (v) => {
        if (v === null || v === undefined)
            return "";
        if (typeof v === "string")
            return v;
        if (typeof v === "number" || typeof v === "boolean")
            return String(v);
        if (typeof v === "object")
            return JSON.stringify(v);
        return "";
    };
    const stringValue = toStringValue(value);
    const numberValue = value !== null && value !== undefined ? Number(value) : "";
    const booleanValue = value === true;
    switch (field.type) {
        case "textarea":
            return (_jsx("textarea", { value: stringValue, onChange: (e) => { onChange(e.target.value); }, placeholder: field.placeholder, disabled: disabled, rows: 4, className: baseInputClass }));
        case "select":
            return (_jsxs("select", { value: stringValue, onChange: (e) => { onChange(e.target.value); }, disabled: disabled, className: baseInputClass, children: [_jsx("option", { value: "", children: "Select..." }), (field.options ?? []).map((opt) => (_jsx("option", { value: opt, children: opt }, opt)))] }));
        case "checkbox":
            return (_jsx("input", { type: "checkbox", checked: booleanValue, onChange: (e) => { onChange(e.target.checked); }, disabled: disabled, className: "h-4 w-4 rounded" }));
        case "radio":
            return (_jsx("div", { className: "space-y-2", children: (field.options ?? []).map((opt) => (_jsxs("label", { className: "flex items-center gap-2 text-sm", children: [_jsx("input", { type: "radio", name: field.name, value: opt, checked: value === opt, onChange: () => { onChange(opt); }, disabled: disabled, className: "h-4 w-4" }), opt] }, opt))) }));
        case "number":
            return (_jsx("input", { type: "number", value: numberValue, onChange: (e) => { onChange(Number(e.target.value)); }, placeholder: field.placeholder, disabled: disabled, className: baseInputClass }));
        case "date":
            return (_jsx("input", { type: "date", value: stringValue, onChange: (e) => { onChange(e.target.value); }, disabled: disabled, className: baseInputClass }));
        default:
            return (_jsx("input", { type: "text", value: stringValue, onChange: (e) => { onChange(e.target.value); }, placeholder: field.placeholder, disabled: disabled, className: baseInputClass }));
    }
}
/**
 * Execution view for the form input step type.
 * Renders the form and handles user submission.
 * @param root0 - The component props
 * @param root0.config - The step configuration
 * @param root0.execution - Current execution state
 * @param root0.onStart - Callback to start the step
 * @param root0.onComplete - Callback to complete the step with data
 * @param root0.isLoading - Whether the step is currently loading
 * @param root0.isAccessible - Whether this step is accessible
 * @returns The execution view component
 */
export function ExecutionView({ config, execution, onStart, onComplete, isLoading, isAccessible, }) {
    const [formValues, setFormValues] = useState(() => {
        // Initialize with default values
        const defaults = {};
        for (const field of config.fields) {
            if (field.defaultValue !== undefined) {
                defaults[field.name] = field.defaultValue;
            }
            else if (field.type === "checkbox") {
                defaults[field.name] = false;
            }
            else {
                defaults[field.name] = "";
            }
        }
        return defaults;
    });
    const isCompleted = execution?.state === "completed";
    const _isInProgress = execution?.state === "in_progress";
    const isPending = !execution || execution.state === "pending";
    /**
     * Starts the step if accessible
     */
    const handleStart = () => {
        if (!isAccessible)
            return;
        onStart();
    };
    /**
     * Handles form submission
     * @param e - the form submit event
     */
    const handleSubmit = (e) => {
        e.preventDefault();
        onComplete({ formValues });
    };
    /**
     * Updates a single field value in the form state
     * @param name - the field name to update
     * @param value - the new value for the field
     */
    const updateField = (name, value) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };
    // Validate required fields
    const isFormValid = config.fields.every((field) => {
        if (!field.required)
            return true;
        const val = formValues[field.name];
        if (val === undefined || val === null || val === "")
            return false;
        return true;
    });
    // Show completed state
    if (isCompleted) {
        const savedValues = execution.data?.formValues ?? {};
        return (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "rounded-lg bg-muted p-4", children: [_jsx("h4", { className: "text-sm font-medium mb-3", children: "Submitted Values" }), _jsx("dl", { className: "space-y-2", children: config.fields.map((field) => (_jsxs("div", { className: "grid grid-cols-3 gap-2 text-sm", children: [_jsxs("dt", { className: "text-muted-foreground", children: [field.label, ":"] }), _jsx("dd", { className: "col-span-2", children: field.type === "checkbox"
                                            ? savedValues[field.name]
                                                ? "Yes"
                                                : "No"
                                            : savedValues[field.name] !== null && savedValues[field.name] !== undefined
                                                ? (typeof savedValues[field.name] === "object"
                                                    ? JSON.stringify(savedValues[field.name])
                                                    : String(savedValues[field.name]))
                                                : "-" })] }, field.name))) })] }), _jsxs("div", { className: "flex items-center gap-2 text-sm text-muted-foreground", children: [_jsx("svg", { className: "h-4 w-4 text-green-500", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M5 13l4 4L19 7" }) }), "Completed"] })] }));
    }
    // Show pending state
    if (isPending) {
        return (_jsxs("div", { className: "space-y-4", children: [_jsx("div", { className: "rounded-lg border border-dashed p-4 text-center", children: _jsx("p", { className: "text-sm text-muted-foreground", children: "Click \"Start\" to fill out the form" }) }), _jsx("button", { onClick: handleStart, disabled: !isAccessible || isLoading, className: `rounded-md px-4 py-2 text-sm font-medium ${isAccessible
                        ? "bg-primary text-primary-foreground hover:bg-primary/90"
                        : "cursor-not-allowed bg-muted text-muted-foreground"} disabled:opacity-50`, children: isLoading ? "Starting..." : "Start" })] }));
    }
    // Show form
    return (_jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [config.fields.map((field) => (_jsxs("div", { className: "space-y-1", children: [_jsxs("label", { className: "text-sm font-medium", children: [field.label, field.required && _jsx("span", { className: "text-destructive ml-1", children: "*" })] }), _jsx(FormFieldRenderer, { field: field, value: formValues[field.name], onChange: (val) => { updateField(field.name, val); }, disabled: isLoading })] }, field.name))), config.fields.length === 0 && (_jsx("p", { className: "text-sm text-muted-foreground py-4 text-center", children: "No form fields configured." })), _jsx("button", { type: "submit", disabled: isLoading === true || !isFormValid, className: "rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50", children: isLoading ? "Submitting..." : config.submitLabel })] }));
}
//# sourceMappingURL=ExecutionView.js.map